import React, { useState, useMemo, type ReactNode } from 'react'

import { LinkOutlined, QuestionCircleOutlined, LockOutlined, EyeOutlined, ColumnHeightOutlined, SwapOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'
import BatchInventoryHoldModal from '@components/shared/inventory/BatchInventoryHoldModal'
import InventoryDetailModal from '@components/shared/inventory/InventoryDetailModal'
import QuickStockAdjustmentModal from '@components/shared/inventory/QuickStockAdjustmentModal'
import TransformationItemModal from '@pages/operator/InventoryTransformations/components/TransformationItemModal'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, TableAction, SupportRule } from '@shared/table/TableIndex'
import { Tag, Tooltip, Space, Button, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  label: string;
  value: string;
  [key: string]: unknown;
}

interface InventoryService extends TableService {
  export: (ids: string[], options: { locale: string }) => Promise<unknown>;
  [key: string]: unknown;
}

interface HoldService {
  [key: string]: unknown;
}

interface InventoriesListProps {
  service: InventoryService;
  role?: 'admin' | 'operator';
  merchantOptions?: MerchantOption[];
  inventoryTypeOptions?: MerchantOption[];
  addJob?: (jobId: string, jobName: string) => void;
  holdService?: HoldService | null;
  merchantId?: string | null;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface InventoryRow {
  id: string;
  product_id?: string;
  product_sku?: string;
  product_name?: string;
  inventory_type?: string;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  quantity?: number;
  available_quantity?: number;
  pending_putaway_quantity?: number;
  allocated_quantity?: number;
  held_quantity?: number;
  status?: string;
  warehouse_name?: string | null;
  merchant_name?: string | null;
  [key: string]: unknown;
}

const InventoriesList: React.FC<InventoriesListProps> = ({
  service, // New prop
  role = 'admin',
  merchantOptions = [],
  inventoryTypeOptions = [],
  addJob,
  holdService = null,
  merchantId = null,
  hideMerchantUI = false, // Operator single-merchant 模式使用
}) => {
  const { t, i18n } = useTranslation()
  const { token } = theme.useToken()
  const [lastModified, setLastModified] = useState(0)
  const [holdModalVisible, setHoldModalVisible] = useState(false)
  const [holdSelectedIds, setHoldSelectedIds] = useState<Set<string>>(new Set())
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailInventory, setDetailInventory] = useState<InventoryRow | null>(null)
  const [quickAdjustVisible, setQuickAdjustVisible] = useState(false)
  const [quickAdjustInventory, setQuickAdjustInventory] = useState<InventoryRow | null>(null)
  const [quickTransformVisible, setQuickTransformVisible] = useState(false)
  const [quickTransformInventory, setQuickTransformInventory] = useState<InventoryRow | null>(null)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  const statusColors: Record<string, string> = {
    in_stock: 'success',
    out_of_stock: 'default',
    blocked: 'error'
  }

  const columns = useMemo<unknown[]>(() => [
    // ── 商品 ──
    {
      nameKey: 'models.inventory.product',
      persist: true,
      sortable: true,
      sortFieldName: 'products.sku',
      width: '250px',
      wrap: true,
      cell: ({ product_id, product_sku, product_name }: InventoryRow) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to={`/products/${product_id}`} style={{ wordBreak: 'break-word' }}>
              {product_name}
            </Link>
            <a
              href={`/${role}/products/${product_id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
          <span style={{ fontSize: '12px', color: '#999' }}>{product_sku}</span>
        </div>
      ),
    },
    // ── 庫存屬性 ──
    {
      name: (
        <Space size={4}>
          {t('models.inventory.inventory_type')}
          <Tooltip title={t('models.inventory.inventory_type_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'inventory_types.name',
      width: '120px',
      wrap: true,
      selector: ({ inventory_type }: InventoryRow) => inventory_type,
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.batch')}
          <Tooltip title={t('models.inventory.batch_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'inventories.batch',
      width: '120px',
      wrap: true,
      selector: ({ batch }: InventoryRow) => batch || '-',
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.expiration_date')}
          <Tooltip title={t('models.inventory.expiration_date_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'inventories.expiration_date',
      width: '120px',
      wrap: true,
      selector: ({ expiration_date }: InventoryRow) => expiration_date || '-',
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.manufacturing_date')}
          <Tooltip title={t('models.inventory.manufacturing_date_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: true,
      sortFieldName: 'inventories.manufacturing_date',
      width: '120px',
      wrap: true,
      selector: ({ manufacturing_date }: InventoryRow) => manufacturing_date || '-',
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.custom_attributes')}
          <Tooltip title={t('models.inventory.custom_attributes_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '200px',
      wrap: true,
      cell: ({ custom_attributes }: InventoryRow) => (
        <CustomAttributesTags attributes={custom_attributes} />
      )
    },
    // ── 數量 ──
    {
      name: (
        <Space size={4}>
          {t('models.inventory.quantity')}
          <Tooltip title={t('models.inventory.quantity_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      selector: ({ quantity }: InventoryRow) => quantity || 0,
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.available_quantity')}
          <Tooltip title={t('models.inventory.available_quantity_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '120px',
      selector: ({ available_quantity }: InventoryRow) => available_quantity || 0,
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.pending_putaway_quantity')}
          <Tooltip title={t('models.inventory.pending_putaway_quantity_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '120px',
      cell: ({ product_id, pending_putaway_quantity }: InventoryRow) => {
        const qty = pending_putaway_quantity ?? 0
        if (qty > 0 && product_id) {
          return <Link to={`/products/${product_id}/inventory`}>{qty}</Link>
        }
        return qty
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.allocated_quantity')}
          <Tooltip title={t('models.inventory.allocated_quantity_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      cell: ({ id, allocated_quantity }: InventoryRow) => {
        const qty = allocated_quantity || 0
        if (role === 'operator' && qty > 0) {
          return <Link to={`/inventories/${id}/orders`}>{qty}</Link>
        }
        return qty
      },
    },
    {
      name: (
        <Space size={4}>
          {t('models.inventory.held_quantity')}
          <Tooltip title={t('models.inventory.held_quantity_tooltip')}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      sortable: false,
      width: '100px',
      cell: ({ held_quantity }: InventoryRow) => {
        const qty = held_quantity || 0
        return qty > 0 ? <Tag color="warning">{qty}</Tag> : qty
      },
    },
    // ── 狀態 ──
    {
      name: (
        <Space size={4}>
          {t('models.inventory.status')}
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t('models.inventory.status_tooltip')}</span>}>
            <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      width: '100px',
      cell: ({ status }: InventoryRow) => (
        <Tag color={(status && statusColors[status]) || 'default'}>
          {t(`enums.inventory.status.${status}`)}
        </Tag>
      ),
    },
    // ── 倉庫 (admin only) ──
    ...(role === 'admin' ? [
      {
        nameKey: 'models.inventory.warehouse',
        sortable: false,
        width: '120px',
        wrap: true,
        selector: ({ warehouse_name }: InventoryRow) => warehouse_name || '-',
      },
    ] : []),
    // ── 貨主 (operator only; single-merchant 模式隱藏) ──
    ...(role === 'operator' && !hideMerchantUI ? [
      {
        nameKey: 'models.inventory.merchant_name',
        persist: true,
        sortable: true,
        sortFieldName: 'merchants.name',
        width: '150px',
        wrap: true,
        selector: ({ merchant_name }: InventoryRow) => merchant_name || '-',
      },
    ] : []),
    // ── 操作 (admin + operator) ──
    {
      name: t('common.actions'),
      width: role === 'operator' ? '200px' : '80px',
      sticky: 'right',
      cell: (row: InventoryRow) => (
        <Space size={0}>
          <Tooltip title={t('shared.inventory_detail.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setDetailInventory(row);
                setDetailModalVisible(true);
              }}
            />
          </Tooltip>
          {holdService && (row.available_quantity ?? 0) > 0 && (
            <Tooltip title={t('shared.inventory_holds.quick_hold')}>
              <Button
                type="text"
                icon={<LockOutlined style={{ color: token.colorWarning }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setHoldSelectedIds(new Set([row.id]));
                  setHoldModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {role === 'operator' && (
            <Tooltip title={t('shared.stock_adjustment.quick_adjust_title')}>
              <Button
                type="text"
                icon={<ColumnHeightOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickAdjustInventory(row);
                  setQuickAdjustVisible(true);
                }}
              />
            </Tooltip>
          )}
          {role === 'operator' && (
            <Tooltip title={t('shared.inventory_transformation.quick_transform_title')}>
              <Button
                type="text"
                icon={<SwapOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickTransformInventory({
                    ...row,
                    product: {
                      id: row.product_id,
                      sku: row.product_sku,
                      name: row.product_name,
                    },
                  } as any);
                  setQuickTransformVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      )
    },
  ], [role, t, holdService, hideMerchantUI, token])

  const supportRules = useMemo<SupportRule[]>(() => [
    ...(role === 'operator' && !hideMerchantUI ? [{
      label: t('models.inventory.merchant_id'),
      component: MultipleValue,
      inputName: 'merchants.id',
      defaultPinned: true,
      createable: false,
      defaultOptions: merchantOptions,
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.merchant_id.title'),
        content: t('shared.inventories.list.filter_tooltips.merchant_id.content'),
      },
    }] : []),
    {
      label: t('models.inventory.product_sku'),
      component: MultipleValue,
      inputName: 'products.sku',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.product_sku.title'),
        content: t('shared.inventories.list.filter_tooltips.product_sku.content'),
      },
    },
    {
      label: t('models.inventory.product_name'),
      component: MultipleValue,
      inputName: 'products.name',
      createable: true,
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.product_name.title'),
        content: t('shared.inventories.list.filter_tooltips.product_name.content'),
      },
    },
    {
      label: t('models.inventory.inventory_type'),
      component: MultipleValue,
      inputName: 'inventory_types.name',
      createable: false,
      defaultOptions: inventoryTypeOptions,
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.inventory_type.title'),
        content: t('shared.inventories.list.filter_tooltips.inventory_type.content'),
      },
    },
    {
      label: t('models.inventory.batch'),
      component: MultipleValue,
      inputName: 'inventories.batch',
      createable: true,
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.batch.title'),
        content: t('shared.inventories.list.filter_tooltips.batch.content'),
      },
    },
    {
      label: t('models.inventory.expiration_date'),
      component: FullDayRangePicker,

      inputName: 'inventories.expiration_date',
      tooltip: {
        title: t('shared.inventories.list.filter_tooltips.expiration_date.title'),
        content: t('shared.inventories.list.filter_tooltips.expiration_date.content'),
      },
    },
    {
      label: t('models.inventory.manufacturing_date'),
      component: FullDayRangePicker,
      inputName: 'inventories.manufacturing_date',
    },
  ], [t, role, merchantOptions, inventoryTypeOptions, hideMerchantUI])

  const handleBatchHold = (selected: Set<string>) => {
    setHoldSelectedIds(selected)
    setHoldModalVisible(true)
  }

  const actions = useMemo<TableAction[]>(() => [
    ...(holdService ? [{
      name: 'shared.inventory_holds.batch_hold',
      icon: <LockOutlined />,
      do: handleBatchHold
    }] : []),
    {
      name: 'common.button.export',
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected: Set<string> | string[]) => executeBatchOperation({
        task: () => service.export(Array.from(selected) as string[], { locale: i18n.language }),
        jobName: 'common.button.export'
      })
    }
  ], [executeBatchOperation, service, holdService, i18n])

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.inventory.product_sku'),
            t('models.inventory.product_name'),
            t('models.inventory.batch'),
            t('models.inventory.custom_attributes'),
          ],
        }}
        columnsEditable={true}
        tableId={`${role.charAt(0).toUpperCase() + role.slice(1)}InventoriesTable`}
        defaultQuery={{
          sorting_field: 'inventories.id',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={actions}
        service={service}
        filterHelpExample={t('shared.inventories.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/inventories/list#features`}
      />

      <InventoryDetailModal
        visible={detailModalVisible}
        inventory={detailInventory}
        inventoryService={service}
        onCancel={() => setDetailModalVisible(false)}
      />

      {holdService && (
        <BatchInventoryHoldModal
          visible={holdModalVisible}
          selectedIds={holdSelectedIds}
          inventoryService={service}
          holdService={holdService}
          merchantId={merchantId ?? undefined}
          onCancel={() => setHoldModalVisible(false)}
          onSuccess={() => setLastModified(Date.now())}
        />
      )}

      {role === 'operator' && (
        <QuickStockAdjustmentModal
          visible={quickAdjustVisible}
          inventory={quickAdjustInventory as any}
          inventoryService={service}
          onCancel={() => setQuickAdjustVisible(false)}
          onSuccess={() => setLastModified(Date.now())}
        />
      )}

      {role === 'operator' && (
        <TransformationItemModal
          standalone
          visible={quickTransformVisible}
          inventory={quickTransformInventory as any}
          onCancel={() => setQuickTransformVisible(false)}
          onSuccess={() => setLastModified(Date.now())}
        />
      )}
    </>
  )
}

export default InventoriesList
